import { useEffect, useState } from "react";
import { api, MODELS } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { GraduationCap, Sparkles, Loader2, CheckCircle2, XCircle, ExternalLink, Trophy, RefreshCw, Printer } from "lucide-react";

const LEVELS = ["beginner","intermediate","advanced"];

export default function Quiz() {
  const [tab, setTab] = useState("play");
  return (
    <div className="max-w-5xl mx-auto p-8">
      <div className="mb-6">
        <div className="overline">Test yourself</div>
        <h1 className="font-display text-5xl mt-2 flex items-center gap-3"><GraduationCap className="w-10 h-10 text-primary"/>Grant Knowledge Quiz</h1>
        <p className="text-muted-foreground mt-2 max-w-2xl">Take short, adaptive quizzes on grantsmanship. Track your score, get explanations, and follow reference links to learn deeper.</p>
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="play" data-testid="quiz-tab-play">Take a quiz</TabsTrigger>
          <TabsTrigger value="history" data-testid="quiz-tab-history">History</TabsTrigger>
        </TabsList>
        <TabsContent value="play"><Player/></TabsContent>
        <TabsContent value="history"><History/></TabsContent>
      </Tabs>
    </div>
  );
}

function Player() {
  const [topic, setTopic] = useState("Grant writing fundamentals");
  const [level, setLevel] = useState("intermediate");
  const [modelIdx, setModelIdx] = useState(0);
  const [busy, setBusy] = useState(false);
  const [quiz, setQuiz] = useState(null);
  const [ans, setAns] = useState({ mcq: [], blanks: [], short: [] });
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const startQuiz = async () => {
    setBusy(true); setResult(null); setQuiz(null);
    try {
      const m = MODELS[modelIdx];
      const { data } = await api.post("/quiz/generate", { topic, level, model_provider:m.provider, model_name:m.name });
      setQuiz(data);
      setAns({ mcq: (data.mcq||[]).map(()=>-1), blanks: (data.blanks||[]).map(()=>""), short: (data.short||[]).map(()=>"") });
    } catch { toast.error("Could not generate a quiz — try another topic"); }
    finally { setBusy(false); }
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/quiz/submit", { quiz_id: quiz.quiz_id, mcq_answers: ans.mcq, blank_answers: ans.blanks, short_answers: ans.short });
      setResult(data); window.scrollTo({ top: 0, behavior: "smooth" });
    } catch { toast.error("Submit failed"); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="mt-6 space-y-6">
      {!quiz && !result && (
        <div className="card-crisp rounded-lg p-6 grid md:grid-cols-4 gap-3 items-end">
          <div className="md:col-span-2"><Label>Topic</Label><Input value={topic} onChange={(e)=>setTopic(e.target.value)} data-testid="quiz-topic"/></div>
          <div><Label>Level</Label><Select value={level} onValueChange={setLevel}><SelectTrigger data-testid="quiz-level"><SelectValue/></SelectTrigger><SelectContent>{LEVELS.map(l=><SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent></Select></div>
          <div><Label>Model</Label>
            <Select value={String(modelIdx)} onValueChange={(v)=>setModelIdx(Number(v))}><SelectTrigger><SelectValue/></SelectTrigger><SelectContent>{MODELS.map((m,i)=><SelectItem key={i} value={String(i)}>{m.label}</SelectItem>)}</SelectContent></Select>
          </div>
          <Button onClick={startQuiz} disabled={busy} className="rounded-full md:col-span-4" data-testid="quiz-start">
            {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin"/>Preparing questions…</> : <><Sparkles className="w-4 h-4 mr-2"/>Start quiz</>}
          </Button>
        </div>
      )}

      {result && <ResultView result={result} onRetry={()=>{ setResult(null); setQuiz(null); }}/>}

      {quiz && !result && (
        <div className="space-y-6">
          <div className="card-crisp rounded-lg p-5">
            <div className="overline">{quiz.topic} · {quiz.level}</div>
            <div className="font-display text-2xl mt-1">Answer thoughtfully — no time pressure.</div>
          </div>
          {(quiz.mcq||[]).map((m,i)=>(
            <div key={`m${i}`} className="card-crisp rounded-lg p-5" data-testid={`quiz-mcq-${i}`}>
              <div className="overline">MCQ {i+1}</div>
              <div className="font-medium mt-1">{m.q}</div>
              <div className="mt-3 space-y-2">
                {(m.options||[]).map((o,j)=>(
                  <label key={j} className={`flex items-center gap-2 p-2 rounded-md border cursor-pointer ${ans.mcq[i]===j?"border-primary bg-primary/5":"border-border hover:bg-secondary"}`}>
                    <input type="radio" name={`mcq-${i}`} checked={ans.mcq[i]===j} onChange={()=>{ const a=[...ans.mcq]; a[i]=j; setAns({...ans, mcq:a}); }} data-testid={`quiz-mcq-${i}-opt-${j}`}/>
                    <span className="text-sm">{o}</span>
                  </label>
                ))}
              </div>
            </div>
          ))}
          {(quiz.blanks||[]).map((b,i)=>(
            <div key={`b${i}`} className="card-crisp rounded-lg p-5" data-testid={`quiz-blank-${i}`}>
              <div className="overline">Fill in the blank {i+1}</div>
              <div className="font-medium mt-1">{b.q}</div>
              <Input className="mt-3" value={ans.blanks[i]||""} onChange={(e)=>{ const a=[...ans.blanks]; a[i]=e.target.value; setAns({...ans, blanks:a}); }} data-testid={`quiz-blank-input-${i}`}/>
            </div>
          ))}
          {(quiz.short||[]).map((s,i)=>(
            <div key={`s${i}`} className="card-crisp rounded-lg p-5" data-testid={`quiz-short-${i}`}>
              <div className="overline">Short answer {i+1}</div>
              <div className="font-medium mt-1">{s.q}</div>
              <Textarea className="mt-3" rows={4} value={ans.short[i]||""} onChange={(e)=>{ const a=[...ans.short]; a[i]=e.target.value; setAns({...ans, short:a}); }} data-testid={`quiz-short-input-${i}`}/>
            </div>
          ))}
          <div className="flex justify-between">
            <Button variant="outline" onClick={()=>{ setQuiz(null); }}>Cancel</Button>
            <Button onClick={submit} disabled={submitting} className="rounded-full" data-testid="quiz-submit">
              {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin"/>Grading…</> : <>Submit &amp; see score</>}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function ResultView({ result, onRetry }) {
  const tierColor = result.pct >= 85 ? "text-emerald-700 bg-emerald-50 border-emerald-300" :
                    result.pct >= 70 ? "text-amber-700 bg-amber-50 border-amber-300" :
                    "text-red-700 bg-red-50 border-red-300";
  return (
    <div className="space-y-5 mt-6" data-testid="quiz-result">
      <div className="card-crisp rounded-lg p-6 flex items-start justify-between gap-6 flex-wrap">
        <div>
          <div className="overline">Your score</div>
          <div className="font-display text-7xl mt-1" style={{fontFeatureSettings:'"lnum" 1, "tnum" 1'}}>{result.pct}<span className="text-3xl text-muted-foreground">%</span></div>
          <div className={`inline-block mt-2 px-3 py-1 rounded-full border text-sm font-medium ${tierColor}`}><Trophy className="w-4 h-4 inline mr-1"/>{result.tier}</div>
          <div className="text-sm text-muted-foreground mt-3">{result.score} / {result.out_of} correct · MCQ {result.breakdown.mcq.score}/{result.breakdown.mcq.out_of} · Blanks {result.breakdown.blanks.score}/{result.breakdown.blanks.out_of} · Short {result.breakdown.short.score}/{result.breakdown.short.out_of}</div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={()=>window.print()}><Printer className="w-4 h-4 mr-1"/>Print</Button>
          <Button onClick={onRetry} className="rounded-full" data-testid="quiz-retry"><RefreshCw className="w-4 h-4 mr-1"/>Take another</Button>
        </div>
      </div>
      {result.improvements?.length > 0 && (
        <div className="card-crisp rounded-lg p-5 border-primary/40 bg-primary/5">
          <div className="font-display text-2xl mb-2">Recommendations</div>
          <ul className="list-disc pl-6 space-y-1 text-sm">{result.improvements.map((s,i)=><li key={i}>{s}</li>)}</ul>
        </div>
      )}
      <AnswersList title="Multiple choice" items={result.answers.mcq} kind="mcq"/>
      <AnswersList title="Fill in the blanks" items={result.answers.blanks} kind="blank"/>
      <AnswersList title="Short answer" items={result.answers.short} kind="short"/>
    </div>
  );
}

function AnswersList({ title, items, kind }) {
  if (!items?.length) return null;
  return (
    <div className="card-crisp rounded-lg p-5">
      <div className="font-display text-2xl mb-3">{title}</div>
      <div className="space-y-3">
        {items.map((it,i) => {
          let ok = false;
          if (kind === "mcq") ok = it.your_index === it.correct_index;
          if (kind === "blank") ok = (it.your_answer||"").toLowerCase().includes((it.correct||"").toLowerCase()) && (it.correct||"").trim() !== "";
          return (
            <div key={i} className="border-l-2 pl-4 py-2" style={{ borderColor: ok ? "#059669" : "#DC2626" }}>
              <div className="flex items-start gap-2">
                {ok ? <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5"/> : <XCircle className="w-4 h-4 text-red-600 mt-0.5"/>}
                <div className="font-medium text-sm">{it.q}</div>
              </div>
              {kind === "mcq" && (
                <div className="mt-2 text-xs">
                  <div>Your answer: <b>{it.options?.[it.your_index] ?? "—"}</b></div>
                  <div>Correct: <b className="text-emerald-700">{it.options?.[it.correct_index]}</b></div>
                </div>
              )}
              {kind === "blank" && (
                <div className="mt-2 text-xs">
                  <div>Your answer: <b>{it.your_answer || "—"}</b></div>
                  <div>Correct: <b className="text-emerald-700">{it.correct}</b></div>
                </div>
              )}
              {kind === "short" && (
                <div className="mt-2 text-xs">
                  <div>Your answer: <i>{it.your_answer || "—"}</i></div>
                  <div className="mt-1"><b>Model answer:</b> {it.model_answer}</div>
                </div>
              )}
              {it.explanation && <div className="mt-1 text-xs text-muted-foreground italic">{it.explanation}</div>}
              {it.reference_url && <a href={it.reference_url} target="_blank" rel="noreferrer" className="text-primary text-xs hover:underline inline-flex items-center gap-1 mt-1">Read more <ExternalLink className="w-3 h-3"/></a>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function History() {
  const [hist, setHist] = useState([]);
  useEffect(() => { api.get("/quiz/history").then(r => setHist(r.data)); }, []);
  return (
    <div className="mt-6">
      {hist.length === 0 ? (
        <div className="card-crisp rounded-lg p-10 text-center text-muted-foreground">No attempts yet. Take a quiz to see your progress here.</div>
      ) : (
        <div className="space-y-2">
          {hist.map(h => (
            <div key={h.id} className="card-crisp rounded-lg p-4 flex items-center gap-4">
              <div className="w-16 h-16 rounded-full grid place-items-center font-display text-2xl" style={{background: h.result.pct>=85?"#DCFCE7":h.result.pct>=70?"#FEF3C7":"#FEE2E2", color: h.result.pct>=85?"#166534":h.result.pct>=70?"#92400E":"#991B1B", fontFeatureSettings:'"lnum" 1'}}>{h.result.pct}%</div>
              <div className="flex-1">
                <div className="font-medium">{h.result.tier}</div>
                <div className="text-xs text-muted-foreground">{h.result.score}/{h.result.out_of} · {new Date(h.created_at).toLocaleString()}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
